/**
 * Modal de detalle de transacción con pista de auditoría.
 *
 * @taskId T475
 * @see functional/Specs/specs_cycle_4.md HU-27-FE §Pantalla 3
 */

import { useTranslation } from 'react-i18next';
import { ArrowDownCircle, ArrowUpCircle, Download, Printer } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Skeleton,
} from '@flexibilitypay/ui';

import { useTransactionDetail } from '../hooks/use-accounts';
import type { TransactionStatus, TransactionSummary, TransactionType } from '../types/account.types';

export interface TransactionDetailModalProps {
  accountId: string | undefined;
  transaction: TransactionSummary | null;
  open: boolean;
  onClose: () => void;
}

const STATUS_BADGE_VARIANTS: Record<TransactionStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  COMPLETED: 'default',
  PENDING: 'secondary',
  FAILED: 'destructive',
  CANCELLED: 'outline',
};

const STATUS_LABELS: Record<TransactionStatus, string> = {
  COMPLETED: 'Completada',
  PENDING: 'Pendiente',
  FAILED: 'Fallida',
  CANCELLED: 'Cancelada',
};

const TYPE_LABELS: Record<TransactionType, string> = {
  CREDIT: 'Crédito',
  DEBIT: 'Débito',
  TRANSFER_IN: 'Transferencia entrante',
  TRANSFER_OUT: 'Transferencia saliente',
  DEBIN: 'DEBIN',
  FEE: 'Comisión',
};

function formatMoney(amount: string): string {
  const num = parseFloat(amount);
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 2,
  }).format(num);
}

function formatDateTime(iso: string): string {
  return new Intl.DateTimeFormat('es-AR', {
    dateStyle: 'long',
    timeStyle: 'medium',
  }).format(new Date(iso));
}

export function TransactionDetailModal({
  accountId,
  transaction,
  open,
  onClose,
}: TransactionDetailModalProps) {
  const { t } = useTranslation(['accounts', 'common']);

  const { data, isLoading } = useTransactionDetail(
    accountId,
    transaction?.id,
  );

  const handlePrint = () => {
    window.print();
  };

  const handleExportPdf = () => {
    // TODO: Implementar exportación PDF
    console.log('Export PDF');
  };

  if (!transaction) return null;

  const isNegative = ['DEBIT', 'TRANSFER_OUT', 'FEE'].includes(transaction.tipo);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>{t('accounts:transactionDetail.title', 'Detalle de Transacción')}</span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={handlePrint}>
                <Printer className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon" onClick={handleExportPdf}>
                <Download className="h-4 w-4" />
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        ) : data ? (
          <div className="space-y-6">
            {/* Monto destacado */}
            <div className="text-center py-4 border-b">
              <div className="flex items-center justify-center gap-2 mb-2">
                {isNegative ? (
                  <ArrowDownCircle className="h-8 w-8 text-red-500" />
                ) : (
                  <ArrowUpCircle className="h-8 w-8 text-green-500" />
                )}
                <span className={`text-4xl font-bold ${isNegative ? 'text-red-600' : 'text-green-600'}`}>
                  {isNegative ? '-' : '+'}{formatMoney(data.monto)}
                </span>
              </div>
              <Badge variant={STATUS_BADGE_VARIANTS[data.estado]} className="text-sm">
                {STATUS_LABELS[data.estado]}
              </Badge>
            </div>

            {/* Información general */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">{t('accounts:transactionDetail.id', 'ID')}</p>
                <p className="font-mono">{data.id}</p>
              </div>
              <div>
                <p className="text-muted-foreground">{t('accounts:transactionDetail.fecha', 'Fecha/Hora')}</p>
                <p>{formatDateTime(data.fecha)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">{t('accounts:transactionDetail.tipo', 'Tipo')}</p>
                <p>{TYPE_LABELS[data.tipo]}</p>
              </div>
              {data.referencia && (
                <div>
                  <p className="text-muted-foreground">{t('accounts:transactionDetail.referencia', 'Referencia')}</p>
                  <p className="font-mono text-xs">{data.referencia}</p>
                </div>
              )}
              {data.concepto && (
                <div className="col-span-2">
                  <p className="text-muted-foreground">{t('accounts:transactionDetail.concepto', 'Concepto')}</p>
                  <p>{data.concepto}</p>
                </div>
              )}
            </div>

            {/* Cards remitente/destinatario */}
            <div className="grid grid-cols-2 gap-4">
              {data.remitente && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">
                      {t('accounts:transactionDetail.remitente', 'Remitente')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-1">
                    <p className="font-medium">{data.remitente.nombre}</p>
                    {data.remitente.cuit && <p className="text-muted-foreground">CUIT: {data.remitente.cuit}</p>}
                    {data.remitente.cvu && <p className="font-mono text-xs">CVU: {data.remitente.cvu}</p>}
                    {data.remitente.cbu && <p className="font-mono text-xs">CBU: {data.remitente.cbu}</p>}
                    {data.remitente.banco && <p className="text-muted-foreground">{data.remitente.banco}</p>}
                  </CardContent>
                </Card>
              )}

              {data.destinatario && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">
                      {t('accounts:transactionDetail.destinatario', 'Destinatario')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-1">
                    <p className="font-medium">{data.destinatario.nombre}</p>
                    {data.destinatario.cuit && <p className="text-muted-foreground">CUIT: {data.destinatario.cuit}</p>}
                    {data.destinatario.cvu && <p className="font-mono text-xs">CVU: {data.destinatario.cvu}</p>}
                    {data.destinatario.cbu && <p className="font-mono text-xs">CBU: {data.destinatario.cbu}</p>}
                    {data.destinatario.banco && <p className="text-muted-foreground">{data.destinatario.banco}</p>}
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Timeline de auditoría */}
            {data.auditTrail && data.auditTrail.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">
                    {t('accounts:transactionDetail.auditTrail', 'Pista de Auditoría')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="relative space-y-4">
                    {data.auditTrail.map((entry, index) => (
                      <div key={index} className="flex gap-4">
                        <div className="relative">
                          <div className="h-3 w-3 rounded-full bg-primary" />
                          {index < data.auditTrail.length - 1 && (
                            <div className="absolute top-3 left-1.5 h-full w-px bg-border -translate-x-1/2" />
                          )}
                        </div>
                        <div className="flex-1 pb-4">
                          <p className="font-medium text-sm">{entry.evento}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatDateTime(entry.timestamp)}
                            {entry.actor && ` - ${entry.actor}`}
                          </p>
                          {entry.detalle && (
                            <p className="text-xs text-muted-foreground mt-1">{entry.detalle}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        ) : (
          <div className="p-8 text-center text-muted-foreground">
            {t('accounts:transactionDetail.notFound', 'Transacción no encontrada.')}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
